import { useState, useEffect, useCallback, useRef } from "react";
import { agentRadarService } from "../../services/agentRadarService";

// Agent Radar state (personal shortlists). One selected list drives the entries
// fetch; position/label/sort go to the server as query params (SCHEMA-PLAN
// contract — the backend filters and sorts, so re-selecting refetches). Lists and
// the owner's label palette load once and are kept fresh after membership moves.
// Membership ops (move/copy/remove) are optimistic on the entries array for a
// snappy UI, then the list counts are refetched for authoritative numbers.
export const useAgentRadar = () => {
  const [lists, setLists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedList, setSelectedList] = useState(null); // list id
  const [loading, setLoading] = useState(false);
  const [listsLoading, setListsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterPos, setFilterPos] = useState("all");
  const [filterLabel, setFilterLabel] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const abortRef = useRef(null);

  // ── Lists + labels (once, plus refetch after count-changing ops) ────────────
  const fetchLists = useCallback(async () => {
    const res = await agentRadarService.getLists();
    if (res.success) {
      setLists(res.data);
      setSelectedList((cur) => {
        if (cur && res.data.some((l) => l.id === cur)) return cur;
        const def = res.data.find((l) => l.is_default) || res.data[0];
        return def ? def.id : null;
      });
    }
    return res;
  }, []);

  const fetchLabels = useCallback(async () => {
    const res = await agentRadarService.getLabels();
    if (res.success) setLabels(res.data);
    return res;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setListsLoading(true);
      await Promise.all([fetchLists(), fetchLabels()]);
      if (alive) setListsLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [fetchLists, fetchLabels]);

  // ── Entries for the selected list (server filters + sorts) ───────────────────
  const fetchEntries = useCallback(async () => {
    if (!selectedList) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const params = { list: selectedList, sort: sortBy };
    if (filterPos !== "all") params.position = filterPos;
    if (filterLabel !== "all") params.label = filterLabel;
    try {
      const res = await agentRadarService.getEntries(params, controller.signal);
      if (res.success) setEntries(res.data);
      else setError(res.error || "Failed to load your radar.");
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      setError("Failed to load your radar. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedList, sortBy, filterPos, filterLabel]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // ── List CRUD ────────────────────────────────────────────────────────────────
  const createList = useCallback(
    async (name) => {
      const res = await agentRadarService.createList(name);
      if (res.success) {
        await fetchLists();
        if (res.data?.id) setSelectedList(res.data.id);
      }
      return res;
    },
    [fetchLists],
  );

  const renameList = useCallback(async (id, name) => {
    const res = await agentRadarService.renameList(id, name);
    if (res.success) {
      setLists((prev) => prev.map((l) => (l.id === id ? { ...l, name } : l)));
    }
    return res;
  }, []);

  const deleteList = useCallback(
    async (id) => {
      const res = await agentRadarService.deleteList(id);
      if (res.success) {
        setSelectedList((cur) => {
          if (cur !== id) return cur;
          const def = lists.find((l) => l.is_default);
          return def ? def.id : null;
        });
        await fetchLists();
        await fetchEntries();
      }
      return res;
    },
    [lists, fetchLists, fetchEntries],
  );

  // ── Membership ops (optimistic entries, then refetch counts) ─────────────────
  const removeEntry = useCallback(
    async (entryId) => {
      const prev = entries;
      setEntries((cur) => cur.filter((e) => e.id !== entryId));
      const res = await agentRadarService.removeEntry(entryId);
      if (res.success) fetchLists();
      else setEntries(prev);
      return res;
    },
    [entries, fetchLists],
  );

  // Move out of the current list → the entry leaves this view. from = selectedList.
  const moveEntry = useCallback(
    async (entryId, targetListId) => {
      const prev = entries;
      setEntries((cur) => cur.filter((e) => e.id !== entryId));
      const res = await agentRadarService.moveEntry(entryId, targetListId, selectedList);
      if (res.success) fetchLists();
      else setEntries(prev);
      return res;
    },
    [entries, fetchLists, selectedList],
  );

  // Copy keeps the entry in the current list; only counts change elsewhere.
  const copyEntry = useCallback(
    async (entryId, targetListId) => {
      const res = await agentRadarService.copyEntry(entryId, targetListId);
      if (res.success) fetchLists();
      return res;
    },
    [fetchLists],
  );

  // ── Label ops on an entry (optimistic on the entries array) ──────────────────
  const patchEntry = useCallback((entryId, patch) => {
    setEntries((cur) => cur.map((e) => (e.id === entryId ? { ...e, ...patch(e) } : e)));
  }, []);

  const assignLabel = useCallback(
    async (entryId, label) => {
      patchEntry(entryId, (e) => ({ labels: [...(e.labels || []), label] }));
      const res = await agentRadarService.assignLabel(entryId, label.id);
      if (!res.success) patchEntry(entryId, (e) => ({ labels: (e.labels || []).filter((l) => l.id !== label.id) }));
      return res;
    },
    [patchEntry],
  );

  const unassignLabel = useCallback(
    async (entryId, label) => {
      patchEntry(entryId, (e) => ({ labels: (e.labels || []).filter((l) => l.id !== label.id) }));
      const res = await agentRadarService.unassignLabel(entryId, label.id);
      if (!res.success) patchEntry(entryId, (e) => ({ labels: [...(e.labels || []), label] }));
      return res;
    },
    [patchEntry],
  );

  const createLabel = useCallback(async ({ name, color }) => {
    const res = await agentRadarService.createLabel({ name, color });
    if (res.success) setLabels((prev) => [...prev, res.data]);
    return res;
  }, []);

  // Add a private note from the detail panel; ticks the card's note count on success.
  const addNote = useCallback(
    async (entryId, text) => {
      const res = await agentRadarService.addNote(entryId, text);
      if (res.success) {
        setEntries((cur) =>
          cur.map((e) => (e.id === entryId ? { ...e, notes_count: (e.notes_count || 0) + 1 } : e)),
        );
      }
      return res;
    },
    [],
  );

  return {
    lists,
    labels,
    entries,
    selectedList,
    setSelectedList,
    loading,
    listsLoading,
    error,
    filterPos,
    setFilterPos,
    filterLabel,
    setFilterLabel,
    sortBy,
    setSortBy,
    fetchEntries,
    fetchLists,
    createList,
    renameList,
    deleteList,
    removeEntry,
    moveEntry,
    copyEntry,
    assignLabel,
    unassignLabel,
    createLabel,
    addNote,
  };
};

export default useAgentRadar;
